"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Section } from "@/components/ui/Section";
import { Typography } from "@/components/ui";

export interface OttFeatureItem {
  subHeading?: string;
  title: string;
  description: string | React.ReactNode;
  imageUrl: string;
  imageAlt: string;
  sectionTitle?: string;
  sectionDescription?: string;
}

interface OttFeaturesSectionProps {
  features: OttFeatureItem[];
  imgBaseUrl: string;
  smartFeaturesTitle?: string;
  smartFeaturesDescription?: string;
  orderListTitle?: string;
  orderList?: Array<string | React.ReactNode>;
}

export default function OttFeaturesSection({
  features,
  imgBaseUrl,
  smartFeaturesTitle,
  smartFeaturesDescription,
  orderListTitle,
  orderList,
}: OttFeaturesSectionProps) {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  return (
    <>
      {(smartFeaturesTitle || smartFeaturesDescription) && (
        <Section variant="default" background="white" style={{ paddingBottom: "0px" }}>
          <div className="text-center max-w-5xl mx-auto">
            {smartFeaturesTitle && (
              <Typography variant="h2" as="h2" className="mb-4">
                {smartFeaturesTitle}
              </Typography>
            )}
            {smartFeaturesDescription && (
              <Typography variant="large" as="p" className="text-text-secondary mb-4">
                {smartFeaturesDescription}
              </Typography>
            )}
          </div>
        </Section>
      )}

      <div>
        {features.map((feature, index) => (
          <React.Fragment key={index}>
            {(feature.sectionTitle || feature.sectionDescription) && (
              <Section variant="default" background="white" style={{ paddingBottom: "0px" }}>
                <div className="text-center max-w-5xl mx-auto">
                  {feature.sectionTitle && (
                    <Typography variant="h2" as="h2" className="mb-4">
                      {feature.sectionTitle}
                    </Typography>
                  )}
                  {feature.sectionDescription && (
                    <Typography variant="large" as="p" className="text-text-secondary mb-4">
                      {feature.sectionDescription}
                    </Typography>
                  )}
                </div>
              </Section>
            )}
            <Section
              variant="default"
              className={index % 2 === 1 ? "bg-gray-50" : "bg-white"}
            >
              <div className={`grid lg:grid-cols-2 gap-6 items-center ${index % 2 === 1 ? "lg:grid-flow-col-dense" : ""}`}>
                <div className={index % 2 === 1 ? "lg:col-start-2" : ""}>
                  {feature.subHeading && (
                    <Typography variant="body" as="p" className="text-sm font-normal mb-6 bg-red-50 px-3 py-1 rounded-lg inline-block">
                      {feature.subHeading}
                    </Typography>
                  )}
                  <Typography variant="h2" as="h2" className="mb-4">
                    {feature.title}
                  </Typography>
                  <Typography variant="body" as="p" className="text-text-secondary font-medium">
                    {isClient && (typeof feature.description === "string" ? (
                      <span dangerouslySetInnerHTML={{ __html: feature.description }} />
                    ) : (
                      feature.description
                    ))}
                  </Typography>
                </div>
                <div className={index % 2 === 1 ? "lg:col-start-1" : ""}>
                  <Image
                    src={imgBaseUrl + feature.imageUrl}
                    alt={feature.imageAlt}
                    title={feature.imageAlt}
                    width={600}
                    height={400}
                    loading="lazy"
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 600px"
                    className="w-full h-auto rounded-lg  max-w-lg mx-auto"
                  />
                </div>
              </div>
            </Section>
          </React.Fragment>
        ))}
      </div>

      {orderList && orderList.length > 0 && (
        <Section variant="default" background="white">
          {orderListTitle && (
            <Typography variant="h2" as="h2" className="mb-4 text-center">
              {orderListTitle}
            </Typography>
          )}
          <ol className="list-decimal pl-6 space-y-2 text-gray-700 max-w-4xl mx-auto">
            {orderList.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ol>
        </Section>
      )}
    </>
  );
}


